
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const HormigonImpreso = () => {
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('HormiPremium')
          .list('Impreso', {
            limit: 6,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (error) throw error;

        if (data) {
          const imageFiles = data
            .filter((file) => {
              const ext = file.name.toLowerCase();
              return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
            })
            .slice(0, 6)
            .map((file) => {
              const fileName = encodeURIComponent(file.name);
              return `${SUPABASE_URL}/storage/v1/object/public/HormiPremium/Impreso/${fileName}`;
            });

          setImages(imageFiles);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    loadImages();
  }, []);

  const features = [
    {
      icon: 'ri-palette-line',
      title: 'Diseños Personalizados',
      description: 'Amplia variedad de moldes y colores para crear espacios únicos',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Alta Durabilidad',
      description: 'Resistente al tráfico intenso y condiciones climáticas extremas',
    },
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Excelente Relación Calidad-Precio',
      description: 'Acabado premium a un coste más económico que materiales naturales',
    },
    {
      icon: 'ri-tools-line',
      title: 'Bajo Mantenimiento',
      description: 'Fácil limpieza y conservación sin necesidad de tratamientos frecuentes',
    },
  ];

  const applications = [
    'Terrazas y patios',
    'Entradas de garaje',
    'Caminos y senderos',
    'Zonas de piscina',
    'Plazas y espacios públicos',
    'Áreas comerciales',
  ];

  return (
    <section id="hormigon-impreso" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-palette-line"></i>
              <span>Diseño Personalizado</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">
              Hormigón Impreso
            </h2>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              El <strong>hormigón impreso</strong> es la solución perfecta para crear <strong>pavimentos decorativos</strong> con texturas de piedra natural, madera o adoquín. Combina la <strong>resistencia del hormigón</strong> con la belleza estética de materiales nobles.
            </p>
            <p className="text-secondary text-lg leading-relaxed mb-8">
              Con una amplia gama de <strong>moldes y colores</strong>, puedes personalizar completamente tu espacio exterior o interior, logrando un acabado único y elegante que se adapta a cualquier estilo arquitectónico.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-blue-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-dark mb-1">{feature.title}</h4>
                    <p className="text-secondary text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/servicios/hormigon-impreso"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Ver detalles completos
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {images.length > 0 ? (
              images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className={`rounded-xl overflow-hidden shadow-lg ${
                    index === 0 ? 'col-span-2 h-80' : 'h-64'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Hormigón Impreso ${index + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))
            ) : (
              <div className="col-span-2 h-80 bg-gray-200 rounded-xl flex items-center justify-center">
                <p className="text-gray-500">Cargando imágenes...</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-dark mb-6">
            Aplicaciones del Hormigón Impreso
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {applications.map((app, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-lg"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-blue-600 font-bold"></i>
                </div>
                <span className="text-dark font-medium">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HormigonImpreso;
